import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { listFeedbackApi, setFeedbackStatusApi } from "../api";
import AdminShell from "../components/AdminShell";
import AppPage from "../components/AppPage";

const CATEGORY_RU = {
  question: "Вопрос", suggestion: "Предложение", bug: "Ошибка",
  partnership: "Сотрудничество", other: "Другое",
};
const CATEGORY_COLOR = {
  question: "primary", suggestion: "warning", bug: "danger",
  partnership: "success", other: "secondary",
};
const STATUS_RU = {
  new: { label: "Новое", cls: "bg-danger" },
  read: { label: "Прочитано", cls: "bg-info text-dark" },
  replied: { label: "Отвечено", cls: "bg-success" },
  closed: { label: "Закрыто", cls: "bg-secondary" },
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("ru-RU", { dateStyle: "short", timeStyle: "short" }); }
  catch { return iso; }
};

export default function AdminFeedback() {
  const { user } = useAuth();
  const [filter, setFilter] = useState("all");
  const [rows, setRows] = useState([]);
  const [err, setErr] = useState("");
  const [expanded, setExpanded] = useState(null);

  const reload = async () => {
    setErr("");
    try { setRows(await listFeedbackApi(filter)); }
    catch (e) { setErr(e?.response?.data?.detail || "Ошибка загрузки"); }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [filter]);

  if (!user?.is_admin) {
    return (
      <AdminShell>
        <div className="alert alert-danger" data-testid="admin-fb-forbidden">
          <i className="bi bi-shield-lock me-2"></i>
          Этот раздел доступен только администратору сайта.
        </div>
      </AdminShell>
    );
  }

  const onChangeStatus = async (fid, newStatus) => {
    try {
      await setFeedbackStatusApi(fid, newStatus);
      await reload();
    } catch { /* noop */ }
  };

  const counts = {
    all: rows.length,
    new: rows.filter(r => r.status === "new").length,
    read: rows.filter(r => r.status === "read").length,
    replied: rows.filter(r => r.status === "replied").length,
    closed: rows.filter(r => r.status === "closed").length,
  };

  return (
    <AdminShell title="Обратная связь">
      <AppPage
        testId="admin-feedback-page"
        bodyScroll="table"
        toolbar={(
          <>
      <div className="btn-group btn-group-sm mb-3 flex-wrap" role="group" data-testid="admin-fb-filters">
        {["all", "new", "read", "replied", "closed"].map(s => (
          <button key={s}
                  className={`btn ${filter === s ? "btn-primary" : "btn-outline-primary"}`}
                  onClick={() => setFilter(s)}
                  data-testid={`fb-filter-${s}`}>
            {s === "all" ? "Все" : STATUS_RU[s].label}
            <span className="badge bg-light text-dark ms-2">{counts[s] ?? 0}</span>
          </button>
        ))}
      </div>

      {err && <div className="alert alert-danger py-2">{err}</div>}
          </>
        )}
      >
      <table className="table align-middle mb-0 card card-soft" data-testid="admin-fb-list">
            <thead className="table-light">
              <tr>
                <th></th>
                <th>Дата</th>
                <th>Категория</th>
                <th>От кого</th>
                <th>E-mail</th>
                <th>Тема</th>
                <th>Статус</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <React.Fragment key={r.feedback_id}>
                  <tr data-testid={`fb-row-${r.feedback_id}`}
                      style={{ cursor: "pointer", background: r.status === "new" ? "#fff8e1" : "" }}
                      onClick={() => setExpanded(expanded === r.feedback_id ? null : r.feedback_id)}>
                    <td>
                      <i className={`bi ${expanded === r.feedback_id ? "bi-chevron-down" : "bi-chevron-right"}`}></i>
                    </td>
                    <td className="small">{fmtDate(r.created_at)}</td>
                    <td>
                      <span className={`badge bg-${CATEGORY_COLOR[r.category]}`}>
                        {CATEGORY_RU[r.category]}
                      </span>
                    </td>
                    <td className="fw-semibold small">{r.name || <span className="text-muted">Аноним</span>}</td>
                    <td className="small">
                      {r.email
                        ? <a href={`mailto:${r.email}`} onClick={(e) => e.stopPropagation()}>{r.email}</a>
                        : <span className="text-muted">—</span>}
                    </td>
                    <td className="small text-truncate" style={{ maxWidth: 260 }}>{r.subject}</td>
                    <td><span className={`badge ${STATUS_RU[r.status]?.cls || "bg-secondary"}`}>{STATUS_RU[r.status]?.label || r.status}</span></td>
                    <td className="text-end" onClick={(e) => e.stopPropagation()}>
                      <select className="form-select form-select-sm"
                              value={r.status}
                              onChange={(e) => onChangeStatus(r.feedback_id, e.target.value)}
                              data-testid={`fb-status-${r.feedback_id}`}
                              style={{ maxWidth: 140 }}>
                        {Object.keys(STATUS_RU).map(k => (
                          <option key={k} value={k}>{STATUS_RU[k].label}</option>
                        ))}
                      </select>
                    </td>
                  </tr>
                  {expanded === r.feedback_id && (
                    <tr data-testid={`fb-expand-${r.feedback_id}`}>
                      <td colSpan={8} style={{ background: "#f8f9fc" }}>
                        <div className="p-3">
                          {r.company && (
                            <div className="small text-muted mb-2">
                              <strong>Компания:</strong> {r.company}
                            </div>
                          )}
                          <div className="mb-2 small text-muted">
                            <strong>Сообщение:</strong>
                          </div>
                          <div style={{
                            background: "white", borderLeft: "4px solid #0b3d91",
                            padding: "12px 16px", borderRadius: 8, whiteSpace: "pre-wrap", lineHeight: 1.6,
                          }}>
                            {r.message}
                          </div>
                          {r.email && (
                            <div className="mt-3">
                              <a href={`mailto:${r.email}?subject=Re: ${encodeURIComponent(r.subject)}`}
                                 className="btn btn-sm btn-primary"
                                 onClick={() => onChangeStatus(r.feedback_id, "replied")}
                                 data-testid={`fb-reply-${r.feedback_id}`}>
                                <i className="bi bi-reply me-1"></i>Ответить по e-mail
                              </a>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={8} className="text-center text-muted py-4">Нет обращений</td></tr>
              )}
            </tbody>
          </table>
      </AppPage>
    </AdminShell>
  );
}
